import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "./Constants";

const DisplayAssignments = (props) => {
  const [assignments, setAssignments] = useState([]);
  const [submissionFiles, setSubmissionFiles] = useState({});
  const [uploading, setUploading] = useState({});
  const [message, setMessage] = useState({});
  const enrollId = props.id;

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/assignments`);
        const filteredData = response.data.filter(item => item.enrollment_id == enrollId);
        setAssignments(filteredData);
      } catch (error) {
        console.error("Failed to fetch assignments", error);
      }
    };
    fetchAssignments();
  }, [enrollId]);

  const handleDownload = (filePath, title) => {
    if (!filePath) return;
      // const url = `${API_BASE_URL.replace('/api', '')}/storage/${filePath}`;
    const url = `${API_BASE_URL.replace('/api', '')}/public/storage/${filePath}`;
    window.open(url, '_blank');
  };

  const handleFileChange = (assignmentId, file) => {
    setSubmissionFiles(prev => ({
      ...prev,
      [assignmentId]: file
    }));
    setMessage(prev => ({
      ...prev,
      [assignmentId]: ""
    }));
  };

  const handleSubmitAssignment = async (assignmentId) => {
    const file = submissionFiles[assignmentId];
    if (!file) {
      setMessage(prev => ({
        ...prev,
        [assignmentId]: "Please select a file to submit."
      }));
      return;
    }

    setUploading(prev => ({
      ...prev,
      [assignmentId]: true
    }));

    const formData = new FormData();
    formData.append("submission_file", file);
    formData.append("_method", "POST"); // For Laravel update method

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/assignments/${assignmentId}/submit`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      // Update the assignment in state
      setAssignments(prev => prev.map(assignment => 
        assignment.id === assignmentId 
          ? { 
              ...assignment, 
              file_path_submission: response.data.file_path_submission,
              submission_date: response.data.submission_date,
              status: response.data.status
            } 
          : assignment
      ));

      setMessage(prev => ({
        ...prev,
        [assignmentId]: "Submission successful!"
      }));

      // Clear the file input
      setSubmissionFiles(prev => ({
        ...prev,
        [assignmentId]: null
      }));

      // Clear file input value
      const fileInput = document.getElementById(`file-input-${assignmentId}`);
      if (fileInput) fileInput.value = "";

    } catch (error) {
      setMessage(prev => ({
        ...prev,
        [assignmentId]: "Submission failed: " + (error.response?.data?.message || error.message)
      }));
    } finally {
      setUploading(prev => ({
        ...prev,
        [assignmentId]: false
      }));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { class: 'badge badge-warning', text: 'Pending' },
      'submitted': { class: 'badge badge-success', text: 'Submitted' },
      'graded': { class: 'badge badge-info', text: 'Graded' },
      'overdue': { class: 'badge badge-error', text: 'Overdue' }
    };
    
    const config = statusConfig[status] || { class: 'badge badge-outline', text: status };
    return <span className={config.class}>{config.text}</span>;
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    return new Date(dateTimeString).toLocaleString();
  };

  const isSubmissionAllowed = (assignment) => {
    if (assignment.status === 'submitted' || assignment.status === 'graded') {
      return false; // Already submitted or graded
    }
    
    if (assignment.due_date) {
      const dueDate = new Date(assignment.due_date);
      const now = new Date();
      return now <= dueDate; // Only allow submission before due date
    }
    
    return true; // No due date, always allowed
  };

  return (
    <div className="overflow-x-auto">
      <table className="table table-zebra w-full">
        <thead>
          <tr>
            <th>Title</th>
            <th>File Type</th>
            <th>Due Date</th>
            <th>Submission Date</th>
            <th>Status</th>
            <th>Download Assignment</th>
            <th>Submit Assignment</th>
          </tr>
        </thead>
        <tbody>
          {assignments.length === 0 ? (
            <tr>
              <td colSpan="7" className="text-center">No assignments found.</td>
            </tr>
          ) : (
            assignments.map((assignment) => (
              <tr key={assignment.id}>
                <td className="font-bold">{assignment.title}</td>
                <td>
                  {assignment.file_path ? (
                    <span className="badge badge-outline">
                      {assignment.file_path.split('.').pop().toUpperCase()}
                    </span>
                  ) : (
                    <span className="text-gray-500">No file</span>
                  )}
                </td>
                <td>
                  {assignment.due_date ? formatDateTime(assignment.due_date) : 'No due date'}
                </td>
                <td>
                  {assignment.submission_date ? formatDateTime(assignment.submission_date) : 'Not submitted'}
                </td>
                <td>{getStatusBadge(assignment.status)}</td>
                <td>
                  {assignment.file_path ? (
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleDownload(assignment.file_path, assignment.title)}
                      title="Download Assignment"
                    >
                      Download
                    </button>
                  ) : (
                    <span className="text-gray-500">No file</span>
                  )}
                </td>
                <td>
                  <div className="space-y-2 min-w-[200px]">
                    {assignment.file_path_submission ? (
                      <div className="space-y-2">
                        <button
                          className="btn btn-sm btn-secondary w-full"
                          onClick={() => handleDownload(assignment.file_path_submission, `${assignment.title}_submission`)}
                          title="Download Submission"
                        >
                          View Submission
                        </button>
                        <div className="text-xs text-success">Already Submitted</div>
                      </div>
                    ) : isSubmissionAllowed(assignment) ? (
                      <div className="space-y-2">
                        <input
                          id={`file-input-${assignment.id}`}
                          type="file"
                          className="file-input file-input-bordered file-input-sm w-full"
                          onChange={(e) => handleFileChange(assignment.id, e.target.files[0])}
                          disabled={uploading[assignment.id]}
                        />
                        <button
                          className="btn btn-sm btn-success w-full"
                          onClick={() => handleSubmitAssignment(assignment.id)}
                          disabled={uploading[assignment.id] || !submissionFiles[assignment.id]}
                        >
                          {uploading[assignment.id] ? (
                            <span className="loading loading-spinner loading-sm"></span>
                          ) : (
                            'Submit'
                          )}
                        </button>
                        {message[assignment.id] && (
                          <div className={`text-xs ${message[assignment.id].includes('failed') ? 'text-error' : 'text-success'}`}>
                            {message[assignment.id]}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-error">
                        {assignment.due_date && new Date(assignment.due_date) < new Date() 
                          ? 'Submission overdue' 
                          : 'Submission not allowed'
                        }
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DisplayAssignments;